import {
  Box,
  Stack,
  StackDivider,
  HStack,
  ButtonGroup,
  Button,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { useTranslation, Trans } from "react-i18next";
import { useState } from "react";
import {
  useAddAppraisalMediaMutation,
  useDeleteAppraisalMediaMutation,
  useUpdateMediaMutation,
  useGetMediaAppraisalByIdQuery,
  MediaInput,
  MediaNode,
} from "graphql/queries/generated/queries";
import ASSET_CONSTANTS from "constants/asset";
import { yupResolver } from "@hookform/resolvers/yup";
import { MediaInputSchema } from "graphql/queries/generated/validation-schema";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { ValidationProvider } from "components/form/ValidationContext";
import { FormInputHook, Table } from "components/ui";
import clearAssetMediaValues from "pages/assets/utils/ClearAssetMediaValues";
import getColumns from "helpers/getColumns";
import {
  DOCUMENT_CUSTOM_FIELDS,
  DOCUMENT_CUSTOM_COLUMNS,
  DOCUMENT_HIDDEN_FIELDS,
} from "pages/assets/shared/externalpagesassetconstants/DatatableConstants";
import { handleMutation } from "middleware/Toaster";

interface AnyObject {
  [key: string]: any;
}

const findLabelByValue = (value: string, array: { value: string; label: string }[]) => {
  const obj = array.find((x) => x.value === value);
  return obj ? obj.label : "";
};

interface DocumentsFormProps {
  appraisalId: string;
}

const DocumentsForm = ({ appraisalId }: DocumentsFormProps) => {
  const { t } = useTranslation();

  const methods = useForm<MediaInput>({
    defaultValues: {
      mediaType: ASSET_CONSTANTS.MEDIA_TYPES[ASSET_CONSTANTS.MEDIA_TYPE_DOCUMENT_POSITION].value,
      name: "",
      file: "",
      description: "",
    },
    resolver: yupResolver(MediaInputSchema()),
    mode: "onChange",
  });

  const { setValue, watch } = methods;

  const { data, loading, error, refetch } = useGetMediaAppraisalByIdQuery({
    variables: {
      id: appraisalId,
    },
  });

  const [selectedId, setSelectedId] = useState<string>(null);
  const [currentFile, setCurrentFile] = useState<string>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [
    addAppraisalMedia,
    {
      data: addAppraisalMediaData,
      loading: addAppraisalMediaLoading,
      error: addAppraisalMediaError,
    },
  ] = useAddAppraisalMediaMutation({ ...handleMutation("Media added!") });
  const [
    updateMedia,
    { data: updateMediaData, loading: updateMediaLoading, error: updateMediaError },
  ] = useUpdateMediaMutation({
    ...handleMutation("Media updated!"),
  });
  const [
    deleteAppraisalMedia,
    {
      data: deleteAppraisalMediaData,
      error: deleteAppraisalMediaError,
      loading: deleteAppraisalMediaLoading,
    },
  ] = useDeleteAppraisalMediaMutation({
    ...handleMutation("Media deleted!"),
  });

  if (!data || !data.appraisal) return null;

  let dataToGetColumns;
  let columns;
  const documents: MediaNode[] = [];

  if (
    data &&
    data.appraisal &&
    data.appraisal.media &&
    data.appraisal.media.edges &&
    data.appraisal.media.edges.length > 0
  ) {
    dataToGetColumns = data.appraisal.media.edges[0].node;

    columns = getColumns({
      data: dataToGetColumns,
      customFields: DOCUMENT_CUSTOM_FIELDS,
      customColumns: DOCUMENT_CUSTOM_COLUMNS,
    });

    for (let i = 0; i < data.appraisal.media.edges.length; i += 1)
      documents.push(data.appraisal.media.edges[i].node as MediaNode);
  }

  const onEditModalOpen = (row: { original: MediaNode }) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    if (
      row &&
      row.original &&
      row.original.id &&
      row.original.name &&
      row.original.description &&
      row.original.file
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      setSelectedId(row.original.id);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      setValue("name", row.original.name);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      setValue("file", row.original.file);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      setValue("description", row.original.description);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      setCurrentFile(row.original.file);

      onOpen();
    }
  };

  const onCloseModal = () => {
    setSelectedId(null);
    setValue("name", "");
    setValue("file", "");
    setValue("description", "");
    setCurrentFile(null);

    onClose();
  };

  const onDelete = async (id: string) => {
    await deleteAppraisalMedia({
      variables: {
        input: {
          mediaId: id,
          id: appraisalId,
        },
      },
    });
  };

  const onViewDocument = (row: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    if (row && row.original && row.original.id && row.original.file) window.open(row.original.file);
  };

  const onSubmit: SubmitHandler<MediaInput> = async (values: AnyObject, e: any) => {
    const mediaData = clearAssetMediaValues(values);

    let response;

    if (selectedId) {
      response = await updateMedia({
        variables: {
          input: {
            mediaData: {
              name: mediaData.name,
              file: mediaData.file,
              description: mediaData.description,
            },
            id: selectedId,
          },
        },
      });

      if (
        response.data &&
        response.data.updateMedia &&
        response.data.updateMedia.media &&
        response.data.updateMedia.media.id
      )
        onCloseModal();
    } else {
      response = await addAppraisalMedia({
        variables: {
          input: {
            mediaData: {
              name: mediaData.name,
              file: mediaData.file,
              description: mediaData.description,
              mediaType: mediaData.mediaType,
            },
            appraisalId,
          },
        },
      });

      if (
        response.data &&
        response.data.addAppraisalMedia &&
        response.data.addAppraisalMedia.appraisal
      )
        onCloseModal();

      await refetch();
    }
  };

  const onError = (errors: any, e: any) => console.log(errors, e);
  const handleSubmit = methods.handleSubmit(onSubmit, onError);
  const mediaType = watch("mediaType");

  return (
    <Stack spacing="4">
      <Table
        hiddenColumns={DOCUMENT_HIDDEN_FIELDS}
        columns={columns}
        data={documents}
        deleteAction={onDelete}
        editAction={onEditModalOpen}
        viewAction={onViewDocument}
      />

      <ButtonGroup pb={6} mt={2} display="block" textAlign="left" variant="outline">
        <Button onClick={onOpen} variant="primary">
          <Trans>Add new appraisal document</Trans>
        </Button>
      </ButtonGroup>

      <Modal closeOnOverlayClick={false} isOpen={isOpen} size="xl" onClose={onCloseModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedId ? (
              <Trans>Update appraisal document</Trans>
            ) : (
              <Trans>Add new appraisal document</Trans>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormProvider {...methods}>
              <ValidationProvider schema={MediaInputSchema()}>
                <form id="addAppraisalMedia" onSubmit={handleSubmit}>
                  <Box maxW="6xl">
                    <Stack spacing="4" divider={<StackDivider />}>
                      <HStack alignItems="flex-end">
                        <FormInputHook label="Document name" name="name" />
                      </HStack>

                      <HStack alignItems="flex-end">
                        <FormInputHook
                          minW="30rem"
                          label="Description"
                          name="description"
                          type="textarea"
                        />
                      </HStack>

                      <HStack alignItems="flex-end">
                        <Button
                          title="Open in new window"
                          onClick={() => window.open(currentFile)}
                          fontWeight="600"
                        >
                          Vedi file
                        </Button>
                        <FormInputHook label="File" name="file.0.file" type="file" />
                      </HStack>
                    </Stack>
                  </Box>
                </form>
              </ValidationProvider>
            </FormProvider>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup pb={6} mt={2} display="block" textAlign="left" variant="outline">
              {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
              <Button
                variant="primary"
                onClick={handleSubmit}
                mr={1}
                isLoading={addAppraisalMediaLoading || updateMediaLoading}
                loadingText="Loading"
              >
                <Trans>Save</Trans>
              </Button>
              <Button onClick={onCloseModal}>
                <Trans>Cancel</Trans>
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default DocumentsForm;
